import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, ApiError, type ArchiveFilters, type ArchiveRow } from '../api';
import { Alert, Badge, Button, Card, EmptyState, PageHeader, TableSkeleton } from '../components/ui';
import { IconClipboard, IconSearch } from '../components/icons';
import { REQUEST_STATUS } from './MyRequests';

const STATUSES = [
  'DRAFT', 'PENDING_APPROVAL', 'CHANGES_REQUESTED', 'APPROVED',
  'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'CANCELLED',
] as const;

/**
 * The record of everything the company has raised.
 *
 * Deliberately not the same screen as "My requests": that one is a working
 * list, this one is what somebody opens two years later to answer "what did we
 * give that person, and who signed it off?". So it is filtered rather than
 * scrolled, it goes back to the beginning, and it exports.
 */
export function Archive() {
  const [filters, setFilters] = useState<ArchiveFilters>({ page: 1 });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.departments() });
  const page = useQuery({
    queryKey: ['archive', filters],
    queryFn: () => api.archive(filters),
    // Keeps the table on screen while a filter change loads, rather than
    // collapsing to a spinner and back on every keystroke.
    placeholderData: keepPreviousData,
  });

  // Any filter change resets to page 1, otherwise you narrow the results and
  // land on an empty page 7.
  const set = (patch: ArchiveFilters) => setFilters({ ...filters, ...patch, page: 1 });

  const active = Boolean(filters.q || filters.type || filters.status || filters.departmentId || filters.from || filters.to);

  // Selection survives paging: the point is picking a handful of records for
  // a bundle, and a filter change or turning the page should not silently
  // drop what was already picked.
  const toggleSelected = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (page.isPending) {
    return (
      <div>
        <PageHeader title="All requests" description="Every enrolment and offboarding, back to the beginning." />
        <TableSkeleton rows={6} columns={6} />
      </div>
    );
  }
  if (page.isError) {
    return <Alert>{page.error instanceof ApiError ? page.error.message : 'Could not open the archive.'}</Alert>;
  }

  const data = page.data;

  return (
    <div>
      <PageHeader
        title="All requests"
        description="Every enrolment and offboarding, back to the beginning. Drafts stay private to whoever raised them."
        actions={
          <>
            <Badge tone="neutral">{data.total} matching</Badge>
            {selected.size > 0 && (
              <>
                <Badge tone="brand">{selected.size} selected</Badge>
                <Button
                  variant="secondary"
                  onClick={() => window.open(api.archiveExportDocumentsUrl([...selected]), '_blank')}
                  disabled={selected.size > 40}
                  title={selected.size > 40 ? 'Select 40 or fewer at a time.' : undefined}
                >
                  Download signed PDFs
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  Clear selection
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              onClick={() => window.open(api.archiveExportUrl(filters), '_blank')}
              disabled={data.total === 0}
            >
              Export CSV
            </Button>
          </>
        }
      />

      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative min-w-0 flex-1 sm:max-w-xs">
            <IconSearch className="pointer-events-none absolute top-2.5 left-3 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') set({ q: search.trim() || undefined });
              }}
              onBlur={() => set({ q: search.trim() || undefined })}
              placeholder="Reference or name…"
              aria-label="Search by reference or employee name"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>

          <Picker
            label="Type"
            value={filters.type ?? ''}
            onChange={(value) => set({ type: value || undefined })}
            options={[
              { value: 'ENROLMENT', label: 'Enrolment' },
              { value: 'OFFBOARDING', label: 'Offboarding' },
            ]}
          />

          <Picker
            label="Status"
            value={filters.status ?? ''}
            onChange={(value) => set({ status: value || undefined })}
            options={STATUSES.map((status) => ({
              value: status,
              label: `${REQUEST_STATUS[status].label} (${data.counts[status] ?? 0})`,
            }))}
          />

          <Picker
            label="Department"
            value={filters.departmentId ?? ''}
            onChange={(value) => set({ departmentId: value || undefined })}
            options={(departments.data?.departments ?? []).map((department) => ({
              value: department.id,
              label: department.name,
            }))}
          />

          <DateBox label="From" value={filters.from ?? ''} onChange={(value) => set({ from: value || undefined })} />
          <DateBox label="To" value={filters.to ?? ''} onChange={(value) => set({ to: value || undefined })} />

          {active && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSearch('');
                setFilters({ page: 1 });
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </Card>

      {data.requests.length === 0 ? (
        <EmptyState
          icon={<IconClipboard />}
          title={active ? 'Nothing matches those filters' : 'Nothing here yet'}
          body={
            active
              ? 'Widen the search, or clear the filters to see everything.'
              : 'Requests appear here as soon as they are raised.'
          }
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-4xl text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                    <th className="w-10 px-5 py-3">
                      <span className="sr-only">Select</span>
                    </th>
                    <th className="px-5 py-3">Reference</th>
                    <th className="px-5 py-3">Employee</th>
                    <th className="px-5 py-3">Department</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Raised by</th>
                    <th className="px-5 py-3">Raised</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((row) => (
                    <Row
                      key={row.id}
                      row={row}
                      selected={selected.has(row.id)}
                      onToggleSelected={() => toggleSelected(row.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {data.pages > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <Button
                size="sm"
                variant="secondary"
                disabled={data.page <= 1}
                onClick={() => setFilters({ ...filters, page: data.page - 1 })}
              >
                Previous
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Page {data.page} of {data.pages} · {data.total} requests
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={data.page >= data.pages}
                onClick={() => setFilters({ ...filters, page: data.page + 1 })}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({
  row,
  selected,
  onToggleSelected,
}: {
  row: ArchiveRow;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const status = REQUEST_STATUS[row.status];

  return (
    <tr className="animate-row border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      <td className="px-5 py-3">
        {row.hasDocument && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select ${row.reference} for download`}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600"
          />
        )}
      </td>
      <td className="px-5 py-3">
        <Link
          to={`/requests/${row.id}`}
          className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
        >
          {row.reference}
        </Link>
        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {row.type === 'OFFBOARDING' ? 'Offboarding' : 'Enrolment'} · {row.itemCount} item
          {row.itemCount === 1 ? '' : 's'}
        </div>
      </td>
      <td className="px-5 py-3 text-slate-900 dark:text-slate-100">{row.employeeName ?? 'N/A'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.department ?? 'N/A'}</td>
      <td className="px-5 py-3">
        <Badge tone={status.tone} dot>
          {status.label}
        </Badge>
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.requestedBy}</td>
      <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
        {new Date(row.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
      </td>
    </tr>
  );
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      >
        <option value="">Any</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DateBox({ label, value, onChange }: { label: string; value: string; onChange: (next: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      />
    </label>
  );
}
